import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  CircleDollarSign,
  DatabaseZap,
  FlaskConical,
  ServerCog,
  LogOut,
  Menu,
  Search,
  ShieldAlert,
  UsersRound,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { GatewayPermission, GatewayPermissionProvider, identityRoles, permissionsForIdentity, roleLabels } from "../auth/permissions";

const navigation: { label: string; items: { to: string; label: string; icon: typeof Activity; permission: GatewayPermission }[] }[] = [
  { label: "工作台", items: [
    { to: "/manage/overview", label: "运行概览", icon: Activity, permission: "overview.read" },
    { to: "/manage/playground", label: "生成测试", icon: FlaskConical, permission: "playground.generate" },
  ] },
  { label: "配置中心", items: [
    { to: "/manage/models", label: "模型目录", icon: DatabaseZap, permission: "models.read" },
    { to: "/manage/providers", label: "上游接入", icon: ServerCog, permission: "providers.read" },
    { to: "/manage/consumers", label: "业务接入", icon: UsersRound, permission: "consumers.read" },
  ] },
  { label: "运行保障", items: [
    { to: "/manage/runtime", label: "运行监控", icon: Activity, permission: "models.read" },
    { to: "/manage/circuits", label: "渠道状态", icon: ShieldAlert, permission: "circuits.read" },
    { to: "/manage/requests", label: "请求排障", icon: Search, permission: "requests.read" },
    { to: "/manage/costs", label: "成本与用量", icon: CircleDollarSign, permission: "costs.read" },
  ] },
];

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const me = useQuery({
    queryKey: ["admin-me"],
    queryFn: api.me,
    retry: false,
  });
  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobileOpen]);

  if (me.isPending) {
    return <main className="session-loading"><div className="oauth-progress" /><span>正在读取登录信息</span></main>;
  }
  if (me.isError || !me.data) {
    return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  const permissions = permissionsForIdentity(me.data);
  const roles = identityRoles(me.data);
  const primaryRole = roles[0] || me.data.role;
  const visibleNavigation = navigation
    .map((group) => ({ ...group, items: group.items.filter((item) => permissions.has(item.permission)) }))
    .filter((group) => group.items.length > 0);

  const logout = async () => {
    await api.logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileOpen ? "is-open" : ""}`}>
        <div className="brand-row">
          <div className="brand-mark">MM</div>
          <div>
            <strong>Moras Moneyking</strong>
            <span>模型网关控制台</span>
          </div>
          <button className="icon-button mobile-only" onClick={() => setMobileOpen(false)} aria-label="关闭导航">
            <X size={18} />
          </button>
        </div>
        <nav className="primary-nav" aria-label="主要导航">
          {visibleNavigation.map((group) => <div className="nav-group" key={group.label}>
            <span className="nav-group-label">{group.label}</span>
            {group.items.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} onClick={() => setMobileOpen(false)}>
                <Icon size={17} />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>)}
        </nav>
        <div className="sidebar-footer">
          <div className="environment-line"><span className="status-dot healthy" />本地环境</div>
          <button className="sidebar-user" onClick={logout} title="退出登录">
            <div className="avatar">{me.data?.username?.slice(0, 1).toUpperCase() || "A"}</div>
            <div><strong>{me.data.username}</strong><span>{roleLabels[primaryRole] || primaryRole}</span></div>
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      {mobileOpen ? <button className="sidebar-scrim mobile-only" onClick={() => setMobileOpen(false)} aria-label="关闭导航" /> : null}

      <main className="main-area">
        <header className="mobile-topbar">
          <button className="icon-button mobile-only" onClick={() => setMobileOpen(true)} aria-label="打开导航">
            <Menu size={20} />
          </button>
          <strong>Moras Moneyking</strong>
        </header>
        <div className="page-frame">
          <GatewayPermissionProvider identity={me.data}><Outlet /></GatewayPermissionProvider>
        </div>
      </main>
    </div>
  );
}
